// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * Turning a pasted reply into blocks, and each block into a file path where the
 * reply gives us enough to do it honestly.
 *
 * TWO THINGS DECIDE WHETHER THIS MODULE IS ANY GOOD.
 *
 * 1. IT DOES NOT SCAN LINES. A `/^```/m` scanner is the obvious implementation
 *    and it is wrong on nine of twelve real CommonMark cases — measured against
 *    markdown-it in this build. It sees ZERO fences in a fence inside a list
 *    item, which is the dominant shape of a multi-file answer; ZERO in a `~~~`
 *    tilde fence, silently dropping the whole file; and it splits a 4-backtick
 *    block wrapping a 3-backtick block into two corrupt halves, which matters
 *    because GitHub Copilot's own prompt MANDATES escalating to four backticks.
 *
 * 2. TRUNCATION IS ARITHMETIC INSIDE markdown-it's COORDINATE SYSTEM, never a
 *    look back at raw document lines. `lines[tok.map[1] - 1]` matched against
 *    /^ {0,3}```/ is the natural-looking rule and it fails two ways: a closing
 *    fence carrying container indentation (inside `- ` or `10. ` or `> `) never
 *    matches `^ {0,3}`, and for a bare unclosed fence at the end of a reply,
 *    `lines[map[1]-1]` IS the opening fence, so it reports the block closed.
 *    Both failures point the wrong way — they invent truncations on ordinary
 *    replies and miss real ones.
 *
 *    The rule that works, verified 23/23 on a controlled set covering every
 *    container and fence shape:
 *
 *        n      = content line count
 *        closed = (tok.map[1] - tok.map[0]) >= n + 2
 *
 *    i.e. the token's own line span has room for BOTH fences.
 */

import MarkdownIt from 'markdown-it';
import type { Token } from 'markdown-it';
import type { ParsedBlock, PathSource } from '../types';
import { languageDisagrees, pathlike, type PathlikeContext } from './pathlike';

const md = new MarkdownIt({ html: false, linkify: false, typographer: false });

/** Count the content lines of a fence token, ignoring its trailing newline. */
function contentLineCount(content: string): number {
  if (content === '') return 0;
  return content.split('\n').length - (content.endsWith('\n') ? 1 : 0);
}

export function isClosed(token: Token): boolean {
  if (!token.map) return true;
  return token.map[1] - token.map[0] >= contentLineCount(token.content) + 2;
}

interface Recovery {
  path: string;
  source: PathSource;
  evidence: string;
}

/* ── P1 / P2: the info string ─────────────────────────────────────────────── */

/**
 * `title=`, `file=`, `filename=`, `path=` in the fence's info string.
 *
 * Kept even though a 26,911-block corpus contained ZERO of them — it is five
 * lines, it cannot false-positive, and it is what a Docusaurus-shaped reply
 * emits. Do not expect it to fire.
 */
function fromInfoAttribute(info: string, ctx: PathlikeContext): Recovery | null {
  const m = /\b(?:title|file|filename|path|filepath)\s*=\s*(?:"([^"]+)"|'([^']+)')/i.exec(info);
  const value = m?.[1] ?? m?.[2];
  if (!value) return null;
  // Through the same gate as every other rule. An earlier version returned the
  // attribute's value directly, on the reasoning that an explicit `title=` is
  // the author saying what they mean — which made P1 the one rule with no shape
  // check at all, and the injection suite found it immediately.
  const r = pathlike(value, ctx);
  return r.ok ? { path: r.path, source: 'info-attribute', evidence: m![0] } : null;
}

/** ```` ```src/app.ts ```` — the whole info string is the path. */
function fromInfoPath(info: string, ctx: PathlikeContext): Recovery | null {
  const first = info.trim().split(/\s+/)[0];
  if (!first) return null;
  const r = pathlike(first, ctx);
  // A bare language id passes `pathlike` only via a known extension, and `ts`
  // is not one — but `c`, `r` and `sql` would sail through as extensionless
  // names if they were in the folder, so require a dot or a slash here.
  if (!r.ok || (!first.includes('/') && !first.includes('.'))) return null;
  return { path: r.path, source: 'info-path', evidence: '```' + info };
}

/* ── P3 / P4: the block's own first line ──────────────────────────────────── */

const COMMENT_OPENERS = String.raw`(?:\/\/+|#+|--|;+|%|<!--|\/\*+|\*)`;

/**
 * `// filepath: src/app.ts` — GitHub Copilot Chat's live production convention.
 *
 * Its own prompt file says: "If the code modifies an existing file or should be
 * placed at a specific location, add a line comment with 'filepath:' and the
 * file path", and its reader accepts both `#` and `//` prefixes for every
 * language. This is the rule most likely to fire on a current-generation reply.
 */
function fromFilepathComment(content: string, ctx: PathlikeContext): Recovery | null {
  const line = firstNonBlankLine(content);
  if (!line) return null;
  const m = new RegExp(`^\\s*${COMMENT_OPENERS}\\s*filepath:\\s*(\\S+?)\\s*(?:-->|\\*\\/)?\\s*$`, 'i').exec(line);
  if (!m) return null;
  const r = pathlike(m[1], ctx);
  return r.ok ? { path: r.path, source: 'filepath-comment', evidence: line.trim() } : null;
}

/** `// src/app.ts` or `# src/app.py` — a bare path in a leading comment. */
function fromPathComment(content: string, ctx: PathlikeContext): Recovery | null {
  const line = firstNonBlankLine(content);
  if (!line) return null;
  const m = new RegExp(`^\\s*${COMMENT_OPENERS}\\s*(\\S+?)\\s*(?:-->|\\*\\/)?\\s*$`).exec(line);
  if (!m) return null;
  const r = pathlike(m[1], ctx);
  return r.ok ? { path: r.path, source: 'path-comment', evidence: line.trim() } : null;
}

/**
 * Aider's convention: "First line: the filename with any originally provided
 * path; no extra markup, punctuation, comments, etc. JUST the filename with
 * path." Measured as one of the two largest signals in a real corpus.
 */
function fromBareFirstLine(content: string, ctx: PathlikeContext): Recovery | null {
  const line = firstNonBlankLine(content);
  if (!line) return null;
  const trimmed = line.trim();
  if (!trimmed.includes('/') && !trimmed.includes('.')) return null;
  const r = pathlike(trimmed, ctx);
  return r.ok ? { path: r.path, source: 'first-line', evidence: trimmed } : null;
}

function firstNonBlankLine(content: string): string | null {
  for (const line of content.split('\n')) if (line.trim()) return line;
  return null;
}

/* ── P5: the line immediately before the fence ────────────────────────────── */

/**
 * `**src/app.ts**`, `` `src/app.ts` ``, `### src/app.ts`, `src/app.ts`,
 * `=== src/app.ts ===`.
 *
 * STRICT AND WHOLE-LINE, ALWAYS. The candidate must occupy the entire line. A
 * version of this rule that scans SENTENCES for path-shaped words is wrong
 * roughly two times in five — it lifts `cv2.erode` out of "then call cv2.erode
 * on the mask" — whereas the whole-line form measured effectively 100% precise
 * on the same corpus. The looseness is the bug, not the convention.
 *
 * The separator delimiter class is `=` ONLY. Written as `[=\-*#~]{2,}` it
 * swallows `**src/app.ts**` and reclassifies every bold line as a separator,
 * which is exactly how a research pass came to believe separators were common
 * when in fact there were none at all.
 */
const P5_RULES: [PathSource, RegExp][] = [
  ['bold-line', /^\*\*(.+?)\*\*$/],
  ['bold-line', /^__(.+?)__$/],
  ['code-line', /^`([^`]+)`$/],
  ['heading-line', /^#{1,6}\s+(.+?)\s*$/],
  ['separator-line', /^={3,}\s*(.+?)\s*={3,}$/],
  ['bare-line', /^(.+)$/],
];

function fromPrecedingLine(preceding: Preceding, ctx: PathlikeContext): Recovery | null {
  // At most two non-blank lines back. Beyond that it is prose about the file,
  // not a label for it.
  let seen = 0;
  for (let i = preceding.lines.length - 1; i >= 0 && seen < 2; i--) {
    const line = preceding.lines[i].trim();
    if (!line) continue;
    seen++;
    for (const [source, re] of P5_RULES) {
      const m = re.exec(line);
      if (!m) continue;
      const r = pathlike(m[1], ctx);
      if (!r.ok) continue;
      // markdown-it hands back a heading's TEXT, with the `#` already consumed
      // by the block parser — so `### src/app.ts` arrives here indistinguishable
      // from a bare line, and would be mislabelled. The token type is the only
      // thing that still knows, and the label is shown to the user as evidence.
      const actual: PathSource = preceding.isHeading && source === 'bare-line' ? 'heading-line' : source;
      return { path: r.path, source: actual, evidence: preceding.isHeading ? `### ${line}` : line };
    }
  }
  return null;
}

/**
 * The text of the block immediately preceding a fence, with container prefixes
 * already stripped.
 *
 * Taken from markdown-it's `inline` token rather than from the raw document,
 * for the same reason the truncation rule is: inside a blockquote or a list item
 * the raw line carries `> ` or four spaces of indent, and every P5 pattern is
 * anchored.
 */
interface Preceding {
  readonly lines: readonly string[];
  readonly isHeading: boolean;
}

function precedingLines(tokens: readonly Token[], fenceIndex: number): Preceding {
  let isHeading = false;
  for (let i = fenceIndex - 1; i >= 0; i--) {
    const t = tokens[i];
    if (t.type === 'heading_close') isHeading = true;
    if (t.type === 'inline') return { lines: t.content.split('\n'), isHeading };
    // Walk back over the closing tags of the preceding block, and over the
    // container openers a fence sits inside; stop at anything else, because a
    // second fence or an hr means there is no label.
    if (t.type.endsWith('_open') || t.type.endsWith('_close')) continue;
    return { lines: [], isHeading: false };
  }
  return { lines: [], isHeading: false };
}

/* ── the ladder ───────────────────────────────────────────────────────────── */

export interface ParseOptions extends PathlikeContext {
  /** Refuse to parse a block larger than this. Defends against a 1 GB block. */
  readonly maxBlockBytes?: number;
  readonly maxBlocks?: number;
}

export function parseReply(reply: string, options: ParseOptions = {}): ParsedBlock[] {
  const maxBlockBytes = options.maxBlockBytes ?? 8 * 1024 * 1024;
  const maxBlocks = options.maxBlocks ?? 500;

  let tokens: Token[];
  try {
    tokens = md.parse(reply, {});
  } catch {
    return [];
  }

  const blocks: ParsedBlock[] = [];
  for (let i = 0; i < tokens.length && blocks.length < maxBlocks; i++) {
    const tok = tokens[i];
    if (tok.type !== 'fence') continue;

    // markdown-it does NOT unescape the info string; its own renderer calls
    // `unescapeAll` before using it. Without this, `title="src/app\_v2.ts"`
    // yields a literal backslash and `src&#47;app.ts` stays entity-encoded.
    const info = md.utils.unescapeAll(tok.info ?? '').trim();
    const content = tok.content ?? '';
    if (content.length > maxBlockBytes) continue;

    const recovered =
      fromInfoAttribute(info, options) ??
      fromInfoPath(info, options) ??
      fromFilepathComment(content, options) ??
      fromPathComment(content, options) ??
      fromBareFirstLine(content, options) ??
      fromPrecedingLine(precedingLines(tokens, i), options);

    blocks.push({
      index: blocks.length,
      info,
      content,
      path: recovered?.path ?? null,
      pathSource: recovered?.source ?? 'none',
      evidence: recovered?.evidence ?? null,
      truncated: !isClosed(tok),
      languageMismatch: recovered ? languageDisagrees(info, recovered.path) : false,
      startLine: tok.map?.[0] ?? 0,
      endLine: tok.map?.[1] ?? 0,
    });
  }
  return blocks;
}

/**
 * Remove the path label from a block's content, when that label is METADATA
 * rather than part of the file.
 *
 * Two of the six conventions put the label INSIDE the block, and they need
 * opposite treatment:
 *
 *   • `// filepath: src/app.ts` is a marker. unfence emits it itself in
 *     `bundle.md`, so a model returning the file faithfully returns it too, and
 *     leaving it would add one copy per round trip. Stripped.
 *   • A BARE first line — Aider's convention, "JUST the filename with path" —
 *     is not valid code in any language. Leaving it writes a file whose first
 *     line is a syntax error. Stripped, and this one is not cosmetic: the
 *     browser dry-run produced exactly that patch, creating `src/validate.js`
 *     whose first line was the text `src/validate.js`.
 *   • An ordinary `// src/app.ts` comment is left exactly where the model put
 *     it. People write that deliberately, it is legal in its language, and
 *     removing it would be editing somebody's code.
 */
export function stripPathHeader(content: string, path: string, source: PathSource): string {
  if (source !== 'filepath-comment' && source !== 'first-line') return content;
  const lines = content.split('\n');
  let index = lines.findIndex((line) => line.trim() !== '');
  if (index < 0) return content;

  const line = lines[index];
  if (source === 'filepath-comment') {
    const m = new RegExp(`^\\s*${COMMENT_OPENERS}\\s*filepath:\\s*(\\S+?)\\s*(?:-->|\\*\\/)?\\s*$`, 'i').exec(line);
    if (!m || m[1] !== path) return content;
  } else if (line.trim() !== path) {
    return content;
  }

  index += 1;
  // Take one blank line after the label too, so the file does not start empty.
  if ((lines[index] ?? '').trim() === '') index += 1;
  return lines.slice(index).join('\n');
}

/**
 * Collapse repeated blocks for the same path, keeping the LAST.
 *
 * A model that regenerates a file mid-answer means the later version; taking the
 * first would silently write the draft it just replaced.
 */
export function collapseDuplicates(blocks: readonly ParsedBlock[]): ParsedBlock[] {
  const lastByPath = new Map<string, number>();
  for (const b of blocks) if (b.path) lastByPath.set(b.path, b.index);
  return blocks.filter((b) => !b.path || lastByPath.get(b.path) === b.index);
}
